import { useTranslation } from 'react-i18next'

import { GhostButton, PrimaryButton } from '@/components/ui-kit/Buttons'
import { MascotBubble, MascotSlot } from '@/components/ui-kit/Mascot'
import { Headline } from '@/components/ui-kit/Primitives'
import { ScreenShell, TopBar } from '@/components/ui-kit/Shell'
import { STAKE } from '@/lib/game'
import { de } from '@/lib/format'

interface ConfirmBetsScreenProps {
    placed: number
    onConfirm: () => void
    onBack: () => void
    /** Laeuft die Abgabe gerade? Dann ist der Knopf gesperrt. */
    submitting?: boolean
}

/**
 * Die Sicherheitsfrage vor der Abgabe (WETTBANDIT-67).
 *
 * Vorher verschwand der Tippschein sofort, sobald alle zehn Spiele entschieden
 * waren - und wer bewusst gar nicht wettete, kam gar nicht erst zur Abgabe.
 * Jetzt steht hier, wie viele Wetten es wirklich sind; bei null fragt der
 * Waschbaer noch einmal nach.
 */
export function ConfirmBetsScreen({
    placed,
    onConfirm,
    onBack,
    submitting = false,
}: ConfirmBetsScreenProps) {
    const { t } = useTranslation()
    const empty = placed === 0

    return (
        <ScreenShell>
            <TopBar title={t('bet.confirm.title')} />

            <div className="-mx-1 wb-scroll flex flex-1 flex-col gap-4 overflow-y-auto px-1 pt-3 pb-8">
                <div className="flex items-center gap-2.5">
                    <MascotSlot pose={empty ? 'lecturing' : 'greeting'} size={88} />
                    <MascotBubble>
                        {empty
                            ? t('bet.confirm.emptyBubble')
                            : t('bet.confirm.bubble', { count: placed })}
                    </MascotBubble>
                </div>

                <div data-testid="confirm-headline">
                    <Headline className="text-xl">
                        {empty
                            ? t('bet.confirm.emptyHeadline')
                            : t('bet.confirm.headline', { count: placed })}
                    </Headline>
                    <p className="mt-1.5 text-sm text-wb-dim">
                        {empty ? t('bet.confirm.emptyBody') : t('bet.confirm.body')}
                    </p>
                </div>

                {!empty && (
                    <div className="rounded-2xl border border-wb-border bg-wb-panel px-4 py-3.5 text-center">
                        <span className="wb-num text-lg font-extrabold text-wb-accent">
                            {t('bet.confirm.stake', { amount: de(placed * STAKE) })}
                        </span>
                    </div>
                )}
            </div>

            <div className="mt-3 flex flex-col gap-2">
                {/* Die Abgabe ist endgueltig - ein zweiter Tipper waehrend der
                    laufenden Anfrage wuerde sie ein zweites Mal ausloesen. */}
                <PrimaryButton onClick={onConfirm} disabled={submitting}>
                    {submitting ? t('bet.confirm.submitting') : t('bet.confirm.submit')}
                </PrimaryButton>
                <GhostButton onClick={onBack} disabled={submitting}>
                    {t('bet.confirm.back')}
                </GhostButton>
            </div>
        </ScreenShell>
    )
}
